import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { buildKeys, fleetMeasurementKeys, scheduleKeys, yawCheckKeys } from "../query-keys";
import type { BuildId, YawCheckId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { yawCheckKeys } from "../query-keys";

type YawCheckCreate = components["schemas"]["YawCheckCreate"];
type YawCheckUpdate = components["schemas"]["YawCheckUpdate"];

export function useYawCheckPreviousEvent(buildId: BuildId) {
  return useQuery({
    queryKey: yawCheckKeys.previousEvent(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/yaw-checks/previous-event",
        { params: { path: { build_id: buildId } } },
      );
      // 204 No Content returns empty data
      if (result.response.status === 204) return null;
      return throwIfError(result, "Failed to fetch previous event yaw check");
    },
    enabled: !!buildId,
  });
}

export function useYawChecks(buildId: BuildId) {
  return useQuery({
    queryKey: yawCheckKeys.byBuild(buildId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/builds/{build_id}/yaw-checks",
        { params: { path: { build_id: buildId } } },
      );
      return throwIfError(result, "Failed to fetch yaw checks");
    },
    enabled: !!buildId,
  });
}

export function useCreateYawCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: YawCheckCreate) => {
      const result = await api.POST(
        "/api/builds/{build_id}/yaw-checks",
        { params: { path: { build_id: buildId } }, body: data },
      );
      return throwIfError(result, "Failed to create yaw check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateYawCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ checkId, data }: { checkId: YawCheckId; data: YawCheckUpdate }) => {
      const result = await api.PATCH(
        "/api/builds/{build_id}/yaw-checks/{check_id}",
        { params: { path: { build_id: buildId, check_id: checkId } }, body: data },
      );
      return throwIfError(result, "Failed to update yaw check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useSignOffYawCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: YawCheckId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/yaw-checks/{check_id}/sign-off",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to sign off yaw check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUnlockYawCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: YawCheckId) => {
      const result = await api.POST(
        "/api/builds/{build_id}/yaw-checks/{check_id}/unlock",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to unlock yaw check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useDeleteYawCheck(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (checkId: YawCheckId) => {
      const result = await api.DELETE(
        "/api/builds/{build_id}/yaw-checks/{check_id}",
        {
          params: { path: { build_id: buildId, check_id: checkId } },
        },
      );
      return throwIfError(result, "Failed to delete yaw check");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(buildId) });
      qc.invalidateQueries({ queryKey: buildKeys.detail(buildId) });
      qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
      qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}
